import React, { useMemo, useState } from "react";
import {
  Box,
  Image,
  Text,
  Grid,
  Input,
  Heading,
  Flex,
  IconButton,
  HStack,
  VStack,
  Badge,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { FormControl } from "@chakra-ui/form-control";
import {
  useDeleteProductMutation,
  useGetProductsQuery,
  useUpdateProductMutation,
  useCreateProductMutation,
} from "../../productApi/productAPI";
import { Product } from "../../types/product";
import { useAuth } from "../../auth/AuthContext";
import { Pencil, Trash2, ArrowUpRight, Plus } from "lucide-react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { ProductFormModal } from "./ProductFormModal";
import { useNavigate } from "react-router-dom";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";

const cardsBorderRadii = "8px";

const MyProductsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: allProducts = [], isLoading, refetch } = useGetProductsQuery();
  const [deleteProduct, { isLoading: deletingProduct }] =
    useDeleteProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const [createProduct] = useCreateProductMutation();
  const { theme } = useCustomTheme();

  const [search, setSearch] = useState("");
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const myProducts = useMemo(
    () =>
      allProducts.filter(
        (product: Product) =>
          product.userId === user?.uid &&
          (product.title || "").toLowerCase().includes(search.toLowerCase()),
      ),
    [allProducts, search, user?.uid],
  );

  const handleDelete = async (id: string) => {
    try {
      await deleteProduct(id).unwrap();

      toast({
        title: "Product deleted",
        status: "success",
        duration: 2400,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Failed to delete product",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdate = async (payload: Partial<Product>) => {
    if (!editingProduct?._id) return;

    try {
      await updateProduct({
        id: editingProduct._id,
        updatedProduct: payload,
      }).unwrap();

      toast({
        title: "Product updated",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      setEditingProduct(null);
    } catch (error) {
      console.error(error);
      toast({
        title: "Failed to update product",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreate = async (payload: Partial<Product>) => {
    try {
      await createProduct(payload as Product).unwrap();
      await refetch();

      toast({
        title: "Product created",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      setIsCreateOpen(false);
    } catch (error) {
      console.error(error);
      toast({
        title: "Failed to create product",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      px={{ base: 4, md: 8 }}
      py={8}
      bg={theme.background}
      color={theme.text}
      minH="100%"
    >
      <Flex
        mb={6}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={4}
      >
        <Box>
          <Heading color={theme.text}>My Products</Heading>
          <Text mt={1} color={theme.textMuted}>
            Manage, edit, and preview your listed products.
          </Text>
        </Box>

        <IconButton
          aria-label="Add product"
          icon={<Plus size={18} />}
          onClick={() => setIsCreateOpen(true)}
          color={theme.textOnPrimary}
          bgImage={theme.gradients?.primaryButton || theme.primary}
          boxShadow={theme.shadowSm}
          borderRadius="5px"
          size="md"
          _hover={{
            bgImage: theme.gradients?.primaryButtonHover || theme.primaryHover,
            transform: "translateY(-1px)",
            boxShadow: theme.shadowMd,
          }}
          _active={{ transform: "translateY(0)" }}
          transition="all 0.2s ease"
        />
      </Flex>

      <FormControl mb={6} maxW="420px">
        <Input
          placeholder="Search your products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          bg={theme.inputBg}
          color={theme.text}
          borderColor={theme.inputBorder}
          borderRadius={cardsBorderRadii}
          _placeholder={{ color: theme.inputPlaceholder }}
          _hover={{ borderColor: theme.borderStrong }}
          _focus={{
            borderColor: theme.inputBorderFocus,
            boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
          }}
        />
      </FormControl>

      {isLoading ? (
        <Flex minH="240px" align="center" justify="center">
          <GlobalSpinner />
        </Flex>
      ) : myProducts.length === 0 ? (
        <Box
          p={6}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
          boxShadow={theme.shadowSm}
        >
          <Text color={theme.textMuted}>No products found.</Text>
        </Box>
      ) : (
        <Grid
          templateColumns={{
            base: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={{ base: 4, md: 5 }}
        >
          {myProducts.map((product) => (
            <Box
              key={product._id}
              role="group"
              position="relative"
              bg={theme.surface}
              border="1px solid"
              borderColor={theme.border}
              borderRadius={cardsBorderRadii}
              overflow="hidden"
              boxShadow={theme.shadowMd}
              cursor="pointer"
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: theme.shadowLg || "0 18px 40px rgba(0,0,0,0.16)",
                borderColor: theme.borderStrong,
              }}
              onClick={() => navigate(`/product/${product._id}`)}
            >
              <Box
                position="absolute"
                inset={0}
                pointerEvents="none"
                bgGradient={
                  theme.name === "dark"
                    ? "linear(to-b, rgba(255,255,255,0.035), transparent 26%, rgba(255,255,255,0.02))"
                    : "linear(to-b, rgba(255,255,255,0.4), transparent 24%, rgba(255,255,255,0.12))"
                }
                zIndex={0}
              />

              <Box position="relative" zIndex={1}>
                <Box position="relative">
                  <Image
                    src={
                      product.imageUrl ||
                      (Array.isArray((product as any).images) &&
                      (product as any).images[0]
                        ? (product as any).images[0]
                        : "https://via.placeholder.com/800x600?text=Product")
                    }
                    alt={product.title}
                    w="100%"
                    h={{ base: "180px", md: "200px" }}
                    objectFit="cover"
                    bg={theme.surfaceAlt}
                  />

                  <Box
                    position="absolute"
                    inset={0}
                    bgGradient="linear(to-t, rgba(0,0,0,0.18), rgba(0,0,0,0.06), rgba(0,0,0,0.01))"
                  />

                  <Flex
                    position="absolute"
                    top={3}
                    left={3}
                    right={3}
                    justify="space-between"
                    align="start"
                    gap={3}
                  >
                    <HStack spacing={2} flexWrap="wrap">
                      <Badge
                        borderRadius={cardsBorderRadii}
                        px={3}
                        py={1}
                        fontSize="10px"
                        fontWeight="800"
                        textTransform="uppercase"
                        bg="rgba(255,255,255,0.92)"
                        color="black"
                        boxShadow="sm"
                      >
                        Product
                      </Badge>

                      {product.category && (
                        <Badge
                          borderRadius={cardsBorderRadii}
                          px={3}
                          py={1}
                          fontSize="10px"
                          fontWeight="800"
                          textTransform="uppercase"
                          bg="rgba(0,0,0,0.55)"
                          color="white"
                          backdropFilter="blur(8px)"
                        >
                          {product.category}
                        </Badge>
                      )}
                    </HStack>

                    <HStack spacing={2}>
                      <IconButton
                        aria-label="Edit product"
                        icon={<Pencil size={16} />}
                        size="sm"
                        borderRadius="5px"
                        bg="rgba(255,255,255,0.94)"
                        color="black"
                        boxShadow="0 10px 28px rgba(0,0,0,0.18)"
                        _hover={{
                          bg: "white",
                          transform: "translateY(-2px)",
                        }}
                        _active={{ transform: "translateY(0)" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingProduct(product);
                        }}
                      />

                      <IconButton
                        aria-label="Delete product"
                        icon={<Trash2 size={16} />}
                        size="sm"
                        borderRadius="5px"
                        bg="#ef4444"
                        color="white"
                        boxShadow="0 10px 28px rgba(239,68,68,0.28)"
                        _hover={{
                          bg: "#dc2626",
                          transform: "translateY(-2px)",
                        }}
                        _active={{ transform: "translateY(0)" }}
                        isLoading={deletingProduct}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(product._id!);
                        }}
                      />
                    </HStack>
                  </Flex>
                </Box>

                <Box p={4}>
                  <VStack align="stretch" spacing={3}>
                    <VStack align="stretch" spacing={1}>
                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        fontWeight="900"
                        lineHeight="1.15"
                        color={theme.text}
                        noOfLines={1}
                      >
                        {product.title}
                      </Text>

                      <Text fontSize="sm" color={theme.textMuted} noOfLines={1}>
                        {product.category || "Uncategorized"}
                      </Text>
                    </VStack>

                    <Flex
                      justify="space-between"
                      align="center"
                      gap={3}
                      wrap="wrap"
                    >
                      <Text
                        fontSize="md"
                        fontWeight="900"
                        color={theme.primary}
                        letterSpacing="-0.02em"
                      >
                        MWK {Number(product.price || 0).toLocaleString()}
                      </Text>
                    </Flex>

                    {!!product.description && (
                      <Text
                        fontSize="sm"
                        color={theme.textMuted}
                        noOfLines={2}
                        lineHeight="1.55"
                      >
                        {product.description}
                      </Text>
                    )}

                    <Flex justify="space-between" align="center">
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color={theme.textSubtle}
                        textTransform="uppercase"
                        letterSpacing="0.08em"
                      >
                        View details
                      </Text>

                      <Flex
                        align="center"
                        justify="center"
                        w="34px"
                        h="34px"
                        borderRadius={cardsBorderRadii}
                        bg={theme.surfaceAlt}
                        border="1px solid"
                        borderColor={theme.border}
                        transition="all 0.25s ease"
                        _groupHover={{
                          transform: "translateX(4px)",
                          borderColor: theme.borderStrong,
                        }}
                      >
                        <ArrowUpRight size={16} color={theme.text} />
                      </Flex>
                    </Flex>
                  </VStack>
                </Box>
              </Box>
            </Box>
          ))}
        </Grid>
      )}

      <ProductFormModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        mode="create"
        userId={user?.uid}
        onSubmit={handleCreate}
      />

      <ProductFormModal
        isOpen={Boolean(editingProduct)}
        onClose={() => setEditingProduct(null)}
        mode="edit"
        product={editingProduct}
        userId={user?.uid}
        onSubmit={handleUpdate}
      />
    </Box>
  );
};

export default MyProductsPage;
